import { Children, useMemo } from "react";
import { augmentElement } from "./augmentElement";
import { isEmptyArray, isFunction, isNil, isString, isUndefined } from "./assertions";
import { resolveChildren } from "./resolveChildren";
import { jsx as _jsx } from "react/jsx-runtime";
const SlotKey = "__slot__";

function slotDecorator(slotName, ElementType) {
  ElementType[SlotKey] = slotName;
  return ElementType;
}

export { slotDecorator as slot };
export function getSlotKey(element) {
  var _ref;

  return (_ref = element.props && element.props["slot"]) !== null && _ref !== void 0 ? _ref : element.type && element.type[SlotKey];
}

function findSlots(children, slots) {
  return Children.toArray(children).reduce((acc, x) => {
    if (!isNil(x)) {
      const slotKey = getSlotKey(x);

      if (!isNil(slotKey) && slots.includes(slotKey)) {
        acc[slotKey] = x;
      }
    }

    return acc;
  }, {});
}

export function getRawSlots(children, slots) {
  if (isNil(children)) {
    return {};
  }

  children = resolveChildren(children);

  if (!isString(children)) {
    return findSlots(children, slots);
  }

  return {
    stringValue: children
  };
}
export function useRawSlots(children, slots) {
  return useMemo(() => getRawSlots(children, slots), [children, slots]);
}
export function getSlots(children, {
  _ = {},
  ...slots
}) {
  if (isNil(children)) {
    return {};
  }

  let slotElements = {};
  children = resolveChildren(children);

  if (!isString(children)) {
    slotElements = findSlots(children, Object.keys(slots));
  }

  const {
    defaultWrapper: Wrapper,
    required
  } = _;

  if (!isNil(required)) {
    let unfulfilledSlots = [];

    if (isFunction(required)) {
      unfulfilledSlots = required(slotElements);
    } else {
      required.forEach(x => {
        if (isUndefined(slotElements[x])) {
          unfulfilledSlots.push(x);
        }
      });
    }

    if (!isNil(unfulfilledSlots) && unfulfilledSlots.length !== 0) {
      throw new Error(`Required slot${unfulfilledSlots.length > 1 ? "s" : ""} ${unfulfilledSlots.map(x => `"${x}"`).join(", ")} must receive a component.`);
    }
  }

  if (!isNil(Wrapper)) {
    if (isEmptyArray(Object.keys(slotElements)) && !isNil(children)) {
      const wrapperSlot = Wrapper[SlotKey];

      if (isNil(wrapperSlot)) {
        throw new Error("A default wrapper must have a slot key.");
      }

      slotElements[wrapperSlot] = /*#__PURE__*/_jsx(Wrapper, {
        children: children
      });
    }
  }

  Object.keys(slotElements).forEach(x => {
    let slotProps = slots[x];

    if (isFunction(slotProps)) {
      slotProps = slotProps(slotElements[x], slotElements);
    }

    if (!isNil(slotProps)) {
      slotElements[x] = augmentElement(slotElements[x], slotProps);
    }
  });
  return slotElements;
}
export function useSlots(children, slots) {
  return useMemo(() => getSlots(children, slots), [children, slots]);
}